import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from 'reactstrap';
import { Form } from '@availity/form';
import EmailEditor from 'react-email-editor';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toastr from 'toastr';
import '@availity/yup';

import * as demsActions from '../../store/dems/actions';
import * as demTplActions from '../../store/demTemplates/actions';
import {
  selectSelectedDem,
  selectDemAiPanelOpen,
  selectDemAiMessages,
  selectDemAiLoading,
  selectDemAiError,
} from '../../selectors/dems';
import {
  selectSelectedDemTemplate,
  selectDemTemplatesLoading,
  selectDemTemplates,
} from '../../selectors/demTemplate';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import FormDemMeta from '../../components/Dems/FormDemMeta';
import MacroInfoPanel from '../../components/DemTemplates/MacroInfoPanel';
import AiChatPanel from '../../components/DemTemplates/AiChatPanel';
import formValidation from '../../components/Dems/formValidation';
import * as campaignsActions from '../../store/campaigns/actions';

const EMAIL_EDITOR_OPTIONS = {
  features: {
    userUploads: true,
  },
};

const normalizeDesign = (design) => {
  if (!design) return design;
  if (typeof design !== 'string') return design;

  try {
    return JSON.parse(design);
  } catch (error) {
    return design;
  }
};

const DemTemplatePickerEffect = ({ demTemplateId, isEditMode, dispatch }) => {
  useEffect(() => {
    if (!isEditMode && demTemplateId) {
      dispatch(demTplActions.fetchDemTemplate(demTemplateId));
    }
  }, [demTemplateId, isEditMode, dispatch]);
  return null;
};

const Editor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const history = useHistory();
  const dispatch = useDispatch();
  const emailEditorRef = useRef(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportJson, setExportJson] = useState('');

  const loading = useSelector((state) => state.Dems.loading);
  const isAiPanelOpen = useSelector(selectDemAiPanelOpen);
  const admin = useSelector((state) => state.Auth.admin);
  const canUseAi = ['ADMIN', 'SUPER_ADMIN'].includes(admin?.role);
  const templateLoading = useSelector(selectDemTemplatesLoading);
  const selectedDem = useSelector(selectSelectedDem);
  const selectedTemplate = useSelector(selectSelectedDemTemplate);
  const demTemplates = useSelector(selectDemTemplates);
  const campaigns = useSelector((state) => state.Campaign.campaigns);

  useEffect(() => {
    dispatch(campaignsActions.syncCampaigns());
    dispatch(demTplActions.syncDemTemplates());
    if (isEditMode) {
      dispatch(demsActions.fetchDem(id));
    }
    return () => {
      dispatch(demsActions.resetState());
      dispatch(demTplActions.resetState());
    };
  }, [dispatch, id, isEditMode]);

  const demTemplateOptions = useMemo(
    () =>
      demTemplates
        .filter((t) => !t.archived)
        .map((t) => ({
          value: t.id,
          label: t.name,
        })),
    [demTemplates],
  );

  const campaignOptions = useMemo(
    () =>
      (campaigns || []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [campaigns],
  );

  const initialValues = useMemo(
    () => ({
      name: selectedDem?.name || '',
      description: selectedDem?.description || '',
      demTemplateId: selectedDem?.demTemplateId || '',
      campaignId: selectedDem?.campaignId || '',
      landingPageUrl: selectedDem?.landingPageUrl || '',
      utmSource: selectedDem?.utmSource || '',
      utmMedium: selectedDem?.utmMedium || '',
      utmCampaign: selectedDem?.utmCampaign || '',
    }),
    [selectedDem],
  );

  const designSource = useMemo(
    () =>
      normalizeDesign(
        isEditMode ? selectedDem?.design : selectedTemplate?.design,
      ),
    [isEditMode, selectedDem?.design, selectedTemplate?.design],
  );

  const loadDesignSource = useCallback(() => {
    if (!designSource || !emailEditorRef.current?.editor) return;
    emailEditorRef.current.editor.loadDesign(designSource);
  }, [designSource]);

  useEffect(() => {
    loadDesignSource();
  }, [loadDesignSource]);

  const onEditorReady = useCallback(() => {
    loadDesignSource();
  }, [loadDesignSource]);

  const handleSave = (formValues) => {
    if (!emailEditorRef.current) return;

    emailEditorRef.current.editor.exportHtml(({ design, html }) => {
      dispatch(
        demsActions.saveDem({
          ...formValues,
          design,
          html,
          ...(isEditMode && { demId: id }),
        }),
      );

      history.push('/dems/list');
    });
  };

  const handlePreview = () => {
    if (!emailEditorRef.current) return;
    emailEditorRef.current.editor.exportHtml(({ html }) => {
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    });
  };

  const handleOpenExportModal = () => {
    if (!emailEditorRef.current?.editor) {
      toastr.error(t('Editor is not ready. Try again in a few seconds.'), '');
      return;
    }

    emailEditorRef.current.editor.exportHtml(({ design }) => {
      if (!design || typeof design !== 'object') {
        toastr.error(t('No design available to export.'), '');
        return;
      }

      setExportJson(JSON.stringify(design, null, 2));
      setIsExportModalOpen(true);
    });
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
  };

  const handleCopyExportJson = async () => {
    try {
      await navigator.clipboard.writeText(exportJson);
      toastr.success(t('JSON copied to clipboard'), '');
    } catch (error) {
      toastr.error(
        t('Automatic copy not available. Copy from the text area.'),
        '',
      );
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={isEditMode ? t('Edit DEM') : t('New DEM')} />
          <Form
            initialValues={initialValues}
            enableReinitialize
            validationSchema={formValidation}
            onSubmit={handleSave}>
            {({ isValid, values }) => {
              const hasDemTemplate = Boolean(values.demTemplateId);
              const waitingTemplate =
                !isEditMode && hasDemTemplate && templateLoading;
              const editorBlockLoading = isEditMode && loading && !selectedDem;

              return (
                <>
                  <DemTemplatePickerEffect
                    demTemplateId={values.demTemplateId}
                    isEditMode={isEditMode}
                    dispatch={dispatch}
                  />
                  <Row className="mb-3 align-items-center">
                    <Col>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={() => history.push('/dems/list')}>
                        <i className="bx bx-arrow-back mr-1" />
                        {t('Back to list')}
                      </Button>
                    </Col>
                    <Col className="text-right">
                      {canUseAi && hasDemTemplate && (
                        <Button
                          color="primary"
                          outline={!isAiPanelOpen}
                          size="sm"
                          className="mr-2"
                          type="button"
                          onClick={() => dispatch(demsActions.toggleAiPanel())}>
                          <i className="bx bx-bot mr-1" />
                          {t('AI Assistant')}
                        </Button>
                      )}
                      <Button
                        color="info"
                        outline
                        size="sm"
                        className="mr-2"
                        type="button"
                        onClick={handlePreview}
                        disabled={
                          !hasDemTemplate ||
                          waitingTemplate ||
                          editorBlockLoading
                        }>
                        <i className="bx bx-show mr-1" />
                        {t('Preview')}
                      </Button>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        className="mr-2"
                        type="button"
                        onClick={handleOpenExportModal}
                        disabled={
                          !hasDemTemplate ||
                          waitingTemplate ||
                          editorBlockLoading
                        }>
                        <i className="bx bx-export mr-1" />
                        {t('Export JSON')}
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        type="submit"
                        disabled={
                          !isValid ||
                          waitingTemplate ||
                          editorBlockLoading ||
                          !hasDemTemplate
                        }>
                        {loading || waitingTemplate || editorBlockLoading ? (
                          <Spinner size="sm" color="light" />
                        ) : (
                          <i className="bx bx-cloud-upload mr-1" />
                        )}
                        {t('Save and publish')}
                      </Button>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="3">
                      <Card>
                        <CardBody>
                          <FormDemMeta
                            demTemplateOptions={demTemplateOptions}
                            campaignOptions={campaignOptions}
                          />
                          <MacroInfoPanel />
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={isAiPanelOpen && hasDemTemplate ? '6' : '9'}>
                      <Card className="p-0" style={{ overflow: 'hidden' }}>
                        {waitingTemplate || editorBlockLoading ? (
                          <div className="p-5 text-center">
                            <Spinner color="primary" />
                          </div>
                        ) : !hasDemTemplate ? (
                          <div className="p-5 text-center text-muted">
                            {t('Select a DEM template to open the editor.')}
                          </div>
                        ) : (
                          <EmailEditor
                            ref={emailEditorRef}
                            onReady={onEditorReady}
                            minHeight="80vh"
                            options={EMAIL_EDITOR_OPTIONS}
                          />
                        )}
                      </Card>
                    </Col>
                    {canUseAi && isAiPanelOpen && hasDemTemplate && (
                      <Col md="3">
                        <AiChatPanel
                          emailEditorRef={emailEditorRef}
                          messagesSelector={selectDemAiMessages}
                          loadingSelector={selectDemAiLoading}
                          errorSelector={selectDemAiError}
                          onSend={(prompt, currentDesign) =>
                            dispatch(
                              demsActions.generateAiDem({
                                prompt,
                                currentDesign,
                              }),
                            )
                          }
                          onUndo={(message) => {
                            dispatch(demsActions.undoLastAiDesign(message.id));
                            if (
                              message.designSnapshotBefore &&
                              emailEditorRef.current?.editor
                            ) {
                              emailEditorRef.current.editor.loadDesign(
                                message.designSnapshotBefore,
                              );
                            }
                          }}
                          onReset={() =>
                            dispatch(demsActions.resetAiConversation())
                          }
                        />
                      </Col>
                    )}
                  </Row>
                  <Modal
                    isOpen={isExportModalOpen}
                    toggle={handleCloseExportModal}
                    size="lg">
                    <ModalHeader toggle={handleCloseExportModal}>
                      {t('Export design JSON')}
                    </ModalHeader>
                    <ModalBody>
                      <p className="text-muted mb-2">
                        {t(
                          'Copy the design JSON and paste it into Codex or Claude Code to generate new designs from this one.',
                        )}
                      </p>
                      <Input
                        type="textarea"
                        rows="12"
                        value={exportJson}
                        readOnly
                      />
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="secondary"
                        outline
                        onClick={handleCloseExportModal}>
                        {t('Close')}
                      </Button>
                      <Button
                        color="primary"
                        onClick={handleCopyExportJson}
                        disabled={!exportJson}>
                        {t('Copy')}
                      </Button>
                    </ModalFooter>
                  </Modal>
                </>
              );
            }}
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Editor;
